import { observable, action, computed, makeObservable } from 'mobx';
import type {
    OpenapiProxyStaffAllTransactionsGetListParamsType,
    OpenapiProxyStaffAllTransactionsGetListResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_staff_all_transactions_get_list';
import { OpenapiProxyStaffFreebetHistoryResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_freebet_history';
import getTime from 'src/utils/periodTime';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { getCurrency, SdkStaff } from 'src/AppState/sdk/staff';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { generateCsv, mkConfig, download } from 'export-to-csv';
import { Amount } from 'src_common/common/amount/Amount';
import { DateTime } from 'src_common/utils/time/time';
import { Common } from 'src/AppState/Common';

import { BonusViewType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { apiRejectWithdrawalRequest } from 'src/api_openapi/v2/yaspa/apiRejectWithdrawal';
import { YASPA_REJECTION_REASON } from 'src/AppState/WithdrawalState';
import { YaspaBankingApiRejectPayoutRequestType } from 'src/api_openapi/v2/yaspa/schemas';
import addError from 'src/utils/error.jsx';

interface DepositHistoryPeriodType {
    startDate: string;
    endDate: string;
}

interface DepositTransactionItemType {
    id: number;
    finalized: string | undefined;
    adjustmentType: string | null | undefined;
    amount: Amount;
}

export interface DepositTransactionsType {
    data: DepositTransactionItemType[];
    total: Amount;
}
export class BankingTabState {
    public depositTransactionsList: Resource<OpenapiProxyStaffAllTransactionsGetListResponse200Type>;
    public manualAdjustmentsList: Resource<OpenapiProxyStaffAllTransactionsGetListResponse200Type>;
    public withdrawalsList: Resource<OpenapiProxyStaffAllTransactionsGetListResponse200Type>;
    public latestWithdrawalsList: Resource<OpenapiProxyStaffAllTransactionsGetListResponse200Type>;
    public freebetsList: Resource<OpenapiProxyStaffFreebetHistoryResponse200Type>;
    public freebetsListBonusEngine: Resource<Array<BonusViewType>>;

    private readonly sdkStaff: SdkStaff;
    private readonly models: ModelsState;

    @observable public period: DepositHistoryPeriodType = {
        startDate: DateTime.current().subtractDays(7).format('YYYY-MM-DDT00:00:00'),
        endDate: DateTime.current().format('YYYY-MM-DDT23:59:59'),
    };

    @observable public showCalendar = false;

    public constructor(
        private readonly customerId: number,
        private readonly common: Common,
        sdkStaff: SdkStaff,
        models: ModelsState
    ) {
        this.sdkStaff = sdkStaff;
        this.models = models;
        this.depositTransactionsList = new Resource(
            async (): Promise<OpenapiProxyStaffAllTransactionsGetListResponse200Type> => {
                const body: OpenapiProxyStaffAllTransactionsGetListParamsType = {
                    requestBody: {
                        customerId: this.customerId.toString(),
                        from: DateTime.from(this.period.startDate)?.format('YYYY-MM-DD') ?? '',
                        to: DateTime.from(this.period.endDate)?.format('YYYY-MM-DD') ?? '',
                        page: 1,
                        perPage: 10,
                        accountAdjustment: true,
                        transactionType: 'deposit',
                    },
                };

                return await this.sdkStaff.postAllTransactionsList(body);
            }
        );

        this.manualAdjustmentsList = new Resource(
            async (): Promise<OpenapiProxyStaffAllTransactionsGetListResponse200Type> => {
                const body: OpenapiProxyStaffAllTransactionsGetListParamsType = {
                    requestBody: {
                        customerId: this.customerId.toString(),
                        from: DateTime.from(this.period.startDate)?.format('YYYY-MM-DD') ?? '',
                        to: DateTime.from(this.period.endDate)?.format('YYYY-MM-DD') ?? '',
                        page: 1,
                        perPage: 10,
                        accountAdjustment: true,
                        transactionType: 'manual-adjustments',
                    },
                };

                return await this.sdkStaff.postAllTransactionsList(body);
            }
        );

        this.withdrawalsList = new Resource(
            async (): Promise<OpenapiProxyStaffAllTransactionsGetListResponse200Type> => {
                const body: OpenapiProxyStaffAllTransactionsGetListParamsType = {
                    requestBody: {
                        customerId: this.customerId.toString(),
                        from: DateTime.from(this.period.startDate)?.format('YYYY-MM-DD') ?? '',
                        to: DateTime.from(this.period.endDate)?.format('YYYY-MM-DD') ?? '',
                        page: 1,
                        perPage: 10,
                        accountAdjustment: true,
                        transactionType: 'withdrawal',
                    },
                };

                return await this.sdkStaff.postAllTransactionsList(body);
            }
        );

        this.latestWithdrawalsList = new Resource(
            async (): Promise<OpenapiProxyStaffAllTransactionsGetListResponse200Type> => {
                const body: OpenapiProxyStaffAllTransactionsGetListParamsType = {
                    requestBody: {
                        customerId: this.customerId.toString(),
                        page: 1,
                        perPage: 10,
                        accountAdjustment: true,
                        transactionType: 'withdrawal',
                        statuses: ['rejected', 'cancelled', 'paid'],
                    },
                };

                return await this.sdkStaff.postAllTransactionsList(body);
            }
        );

        this.freebetsList = new Resource(async (): Promise<OpenapiProxyStaffFreebetHistoryResponse200Type> => {
            const body = {
                customer_id: this.customerId,
                requestBody: {
                    startDate: DateTime.from(this.period.startDate)?.format('YYYY-MM-DD') ?? '',
                    endDate: DateTime.from(this.period.endDate)?.format('YYYY-MM-DD') ?? '',
                },
            };

            return await this.sdkStaff.getFreebetHistory(body);
        });

        this.freebetsListBonusEngine = new Resource(async (): Promise<Array<BonusViewType>> => {
            const response = await this.common.trpcClient.client.bonusCampaignsRouter.getBonuses.mutate({
                accountId: this.customerId,
            });

            if (response.status === 200) {
                return response.body['free-bet'] ?? [];
            } else {
                console.error('Bonus engine response status is not 200', response);
                return [];
            }
        });

        makeObservable(this);
    }

    public refreshFreebetLists = async (): Promise<void> => {
        await this.freebetsList.refresh();
        await this.freebetsListBonusEngine.refresh();
    };

    public refreshLists = async (): Promise<void> => {
        await this.depositTransactionsList.refresh();
        await this.manualAdjustmentsList.refresh();
        await this.withdrawalsList.refresh();
        await this.latestWithdrawalsList.refresh();

        await this.refreshFreebetLists();
    };

    public cancelWithdrawal = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): Promise<void> => {
        const id = this.convertDataId(event);
        const { methodType, providerReference } = this.convertYaspaDetails(event);

        if (methodType === 'open-banking') {
            await this.rejectYaspa(id, providerReference, YASPA_REJECTION_REASON.CANCELLED_BY_STAFF);
        } else {
            await this.sdkStaff.patchTransactionStatusUpdate({
                transaction_id: id,
                customer_id: this.customerId,
                transaction_type: 'withdrawals',
                requestBody: {
                    status: 'cancelled',
                },
            });
        }
        await this.withdrawalsList.refresh();
        await this.latestWithdrawalsList.refresh();
    };

    public rejectWithdrawal = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): Promise<void> => {
        const id = this.convertDataId(event);
        const { methodType, providerReference } = this.convertYaspaDetails(event);

        if (methodType === 'open-banking') {
            await this.rejectYaspa(id, providerReference, YASPA_REJECTION_REASON.REJECTED_BY_STAFF);
        } else {
            await this.sdkStaff.patchTransactionStatusUpdate({
                transaction_id: id,
                customer_id: this.customerId,
                transaction_type: 'withdrawals',
                requestBody: {
                    status: 'rejected',
                },
            });
        }
        await this.withdrawalsList.refresh();
        await this.latestWithdrawalsList.refresh();
    };

    private async rejectYaspa(
        transactionId: number,
        citizenTransactionId: string,
        rejectionReason: YaspaBankingApiRejectPayoutRequestType['rejectionReason']
    ): Promise<void> {
        const response = await apiRejectWithdrawalRequest(
            this.common.appEnvParams.gatewayUrl,
            {
                customerId: this.customerId.toString(),
                requestBody: {
                    transactionId,
                    citizenTransactionId,
                    rejectionReason,
                },
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );

        if (response.status !== 200) {
            addError(response.body.errors?.[0]?.message ?? '');
        }
    }

    public cancelBonusBonusEngine = async (bonusId: string): Promise<void> => {
        await this.common.trpcClient.client.bonusCampaignsRouter.cancelBonus.mutate({
            accountId: this.customerId,
            bonusId,
        });

        await this.refreshFreebetLists();
    };

    private convertDataId = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): number => {
        const id = event.currentTarget.getAttribute('data-id');
        const convertedId = parseFloat(id ?? '');

        if (isNaN(convertedId)) {
            throw new Error('Invalid Id');
        }
        return convertedId;
    };

    private convertYaspaDetails = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ): {
        methodType: string;
        providerReference: string;
    } => {
        const methodType = event.currentTarget.getAttribute('data-method-type');
        const convertedMethodType = methodType ?? '';

        const providerReference = event.currentTarget.getAttribute('data-provider-reference');
        const convertedProviderReference = providerReference ?? '';

        return {
            methodType: convertedMethodType,
            providerReference: convertedProviderReference,
        };
    };

    @action public changeTime = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        const newTime = getTime(event.target.value);
        if (newTime !== undefined) {
            this.period = newTime;
        }

        await this.refreshLists();
    };

    @action public onCalendarSubmit = async (calendarPeriod: { startDate: string; endDate: string }): Promise<void> => {
        this.period = {
            startDate: DateTime.from(calendarPeriod.startDate)?.format('YYYY-MM-DDT00:00:00') ?? '',
            endDate: DateTime.from(calendarPeriod.endDate)?.format('YYYY-MM-DDT23:59:59') ?? '',
        };

        await this.refreshLists();
    };

    @action public onCalendarClick = (): void => {
        this.showCalendar = !this.showCalendar;
    };

    @computed public get depositTransactions(): DepositTransactionsType | null {
        const depositTransactionsList = this.depositTransactionsList.getReady();

        if (depositTransactionsList === null) {
            return depositTransactionsList;
        }

        const filteredTransactions = depositTransactionsList.results.filter((item) => item.status === 'paid');

        const total = filteredTransactions.reduce((prev, curr) => {
            return prev.add(new Amount(curr.amount));
        }, new Amount('0'));

        const mapedData = filteredTransactions.map(({ id, finalizedAt, amount, tags, details: { paymentMethod } }) => {
            return {
                id,
                finalized: DateTime.from(finalizedAt ?? '')?.format('YYYY-MM-DD'),
                adjustmentType: paymentMethod?.type === 'open-banking' ? paymentMethod.type : tags.adjustmentType,
                amount: new Amount(amount),
            };
        });

        return {
            data: mapedData,
            total,
        };
    }

    @computed public get currency(): CurrencyType {
        return this.models.getUserModel(this.customerId).walletData.data?.currency ?? 'XTS';
    }

    @computed public get latestWithdrawals(): OpenapiProxyStaffAllTransactionsGetListResponse200Type | null {
        const result = this.latestWithdrawalsList.get();

        if (result.type === 'ready') {
            return result.value;
        }

        return null;
    }

    public queryAudits = async (onQueryAudits: () => void): Promise<void> => {
        await this.refreshLists();
        onQueryAudits();
    };

    @action public onGenerateCSV = (): void => {
        const depositTransactions = this.depositTransactions;

        if (depositTransactions === null || depositTransactions.data.length === 0) {
            return;
        }

        const headers = [
            { key: 'id', displayLabel: 'ID' },
            { key: 'date', displayLabel: 'Date' },
            { key: 'payment', displayLabel: 'Payment' },
            { key: 'amount', displayLabel: 'Amount' },
        ];

        const csvData = depositTransactions.data.map(({ id, finalized, adjustmentType, amount }) => ({
            id,
            date: finalized ?? '-',
            payment: adjustmentType ?? '-',
            amount: amount.format(getCurrency(this.currency)),
        }));

        const options = {
            title: 'Deposit History',
            filename: 'deposit-history',
            showLabels: true,
            columnHeaders: headers,
        };

        const csvExporter = mkConfig(options);
        const csv = generateCsv(csvExporter)(csvData);
        download(csvExporter)(csv);
    };
}
